<template>
  <div
    class="c-graph c-graph--keyword"
    :class="{ 'is-error': isError, 'is-loading': isLoading || isDrawing }"
    ref="canvas"
  >
    <Loading v-if="isLoading || isDrawing" transparent />
    <template v-if="!isLoading && isError">
      <Error v-if="!hasKeyword" img="require" transparent>
        <template slot="title">
          キーワードを登録してください。
        </template>
      </Error>
      <Error v-else-if="!hasData" transparent>
        <template slot="title">
          指定期間の順位データがありません。
        </template>
        <ul>
          <li><span class="c-text--accent">期間指定</span>をしてください。</li>
          <li>
            キーワード登録直後は順位がありません。<br />
            <span class="c-text--accent">翌日</span>ご確認ください。
          </li>
        </ul>
      </Error>
    </template>
    <svg class="c-graph__svg" ref="svg">
      <defs ref="defs">
        <GraphMask :size="size" />
      </defs>
      <g class="c-graph-label" ref="gLabel"></g>
      <g class="c-graph-bg" clip-path="url(#clipPath)">
        <g class="c-graph-bg-pointer" ref="gBgPointer"></g>
        <GraphBg
          :data="x.bg"
          direction="x"
          :scale="d3.x"
          :size="size"
          :transition="transition"
          :isDrawed="isDrawed"
          ref="gBgX"
        />
        <GraphBg
          v-if="y.display.length"
          :data="y[y.display[0].name].bg"
          direction="y"
          :scale="d3.y[y.display[0].name]"
          :size="size"
          :transition="transition"
          :isDrawed="isDrawed"
          ref="gBgY"
        />
      </g>
      <g class="c-graph-bar" clip-path="url(#clipPath)" ref="gBar"></g>
      <g class="c-graph-line" clip-path="url(#clipPath)" ref="gLine"></g>
      <g
        class="c-graph-line c-graph-line--empty"
        clip-path="url(#clipPath)"
        ref="gLineEmpty"
      ></g>
      <g class="c-graph-marker" clip-path="url(#clipPath)" ref="gMarker"></g>
      <GraphFrame :size="size" hasLine />
      <g class="c-graph-pointer" clip-path="url(#clipPath)" ref="gPointer">
        <rect
          x="0"
          y="0"
          :height="size.canvasH"
          :width="size.canvasW"
          class="c-graph-pointer__area"
        />
      </g>
      <g class="c-graph-axis">
        <GraphAxis
          :data="x.axis"
          position="bottom"
          :scale="d3.x"
          :scaleType="x.scaleType"
          :size="size"
          :transition="transition"
          :isDrawed="isDrawed"
          ref="gAxisX"
        />
        <GraphAxis
          v-if="y.display.length"
          :data="y[y.display[0].name].axis"
          position="left"
          :scale="d3.y[y.display[0].name]"
          :scaleType="y[y.display[0].name].scaleType"
          :size="size"
          :transition="transition"
          :isDrawed="isDrawed"
          ref="gAxisYL"
        />
        <GraphAxis
          v-if="y.display.length > 1"
          :data="y[y.display[1].name].axis"
          position="right"
          :scale="d3.y[y.display[1].name]"
          :scaleType="y[y.display[1].name].scaleType"
          :size="size"
          :transition="transition"
          :isDrawed="isDrawed"
          ref="gAxisYR"
        />
      </g>
    </svg>
    <GraphTooltip
      :position="{ x: tooltip.x, y: tooltip.y }"
      :size="size"
      :isVisible="tooltip.isVisible && !isError ? true : false"
    >
      <template slot="title">
        {{ tooltip.data.date | dateText('YYYY年MM月DD日(dd)') }}
      </template>
      <div class="c-graph-tooltip__inner">
        <ul v-if="checked.length" class="c-graph-tooltip-list--summary">
          <li
            v-for="path in pathes"
            :key="path"
            class="c-graph-tooltip-list--summary__item"
            :class="{ 'is-hidden': checked.indexOf(path) === -1 }"
          >
            <span
              class="c-graph-tooltip-list--summary__key"
              :class="
                'c-graph-tooltip-list--summary__key--' + itemData[path].color
              "
            >
              <template v-if="path === 'conversion'">
                CV合計値
              </template>
              <template v-else>
                {{ itemData[path].label }}
              </template>
            </span>
            <span
              v-if="!tooltip.data.isEmpty"
              class="c-graph-tooltip-list--summary__value"
            >
              <template v-if="itemData[path].type === 'number'">
                {{
                  tooltip.data[path]
                    | roundFloatingPointNumber(2)
                    | separateNumber
                }}
              </template>
              <template
                v-else-if="
                  itemData[path].type === 'percentage' ||
                    itemData[path].type === 'rank'
                "
              >
                {{ tooltip.data[path] | roundFloatingPointNumber(2) }}
              </template>
              <template v-else>
                {{ tooltip.data[path] }}
              </template>
              <template v-if="tooltip.data[path] || tooltip.data[path] === 0">
                {{ itemData[path].unit }}
              </template>
            </span>
          </li>
          <li
            v-for="path in cvPathes"
            :key="path"
            class="c-graph-tooltip-list--summary__item"
            :class="{ 'is-hidden': checked.indexOf('conversion') === -1 }"
          >
            <span
              class="c-graph-tooltip-list--summary__key c-graph-tooltip-list--summary__key--sub"
              :class="
                'c-graph-tooltip-list--summary__key--' +
                  itemData.conversion.children[path].color
              "
            >
              {{ itemData.conversion.children[path].label }}
            </span>
            <span
              v-if="!tooltip.data.isEmpty"
              class="c-graph-tooltip-list--summary__value"
            >
              {{
                tooltip.data[path]
                  | roundFloatingPointNumber(2)
                  | separateNumber
                  | nullToZero
              }}
            </span>
          </li>
        </ul>
        <div v-if="tooltip.data.isEmpty" class="c-graph-tooltip-guide">
          <p class="c-graph-tooltip-guide__text">
            GSCに<br />データ反映後<br />表示されます
          </p>
        </div>
      </div>
    </GraphTooltip>
    <transition name="v-fade-in">
      <ul
        v-if="!isLoading && !isDrawing && hasKeyword && hasData"
        class="c-graph-task-marker"
      >
        <li
          v-for="(item, index) in dateTasks"
          :key="index"
          class="c-graph-task-marker__item"
          :data-date="item.date | dateText('YYYY.MM.DD')"
          :style="{ left: d3.x(item.date) + 'px' }"
          @click="toggleTask(item)"
          ref="taskMarker"
          @mouseover="mouseOverTaskMarker($event, item.date, d3.x(item.date))"
        >
          <span></span>
        </li>
      </ul>
    </transition>

    <template v-if="hasKeyword">
      <div class="c-graph-task">
        <template v-if="hasKeyword && validTasks && validTasks.length > 0">
          <div
            class="c-graph-task__header c-graph-task__header--active"
            ref="btnTaskList"
            @click="closeTaskList"
          >
            <p class="c-text--ellipsis">
              施策のログ（{{ validTasks.length }}件） キーワード:
              {{ selected.keyword }} URL: {{ removedUrl }}
            </p>
            <p class="c-toggle"></p>
          </div>
          <div
            class="c-graph-task-list-container c-graph-task-list-container--active"
            ref="taskList"
            @mouseleave="mouseoutTaskList"
          >
            <perfect-scrollbar
              ref="scroll"
              :options="{ suppressScrollX: false, minScrollbarLength: 10 }"
            >
              <ul class="c-graph-task-list">
                <template v-for="dateTask in dateTasks">
                  <template v-for="(item, index) in dateTask.items">
                    <li
                      :class="{
                        'c-graph-task-list__item': true,
                        'c-graph-task-list__item--is-selected': isSelectTask(
                          dateTask.date
                        )
                      }"
                      :key="'item-task-' + item.id + '-' + index"
                      :data-date="dateTask.date | dateText('YYYY.MM.DD')"
                      ref="taskListItem"
                      @click="toggleTask(dateTask)"
                      @mouseover="mouseoverTaskList(dateTask.date)"
                    >
                      <p class="c-graph-task-list__date">
                        <template v-if="index === 0">
                          {{ dateTask.date | dateText('YYYY.MM.DD') }}
                        </template>
                      </p>
                      <p :class="categoryCssClass(item)">
                        <span>
                          {{ category(item) }}
                        </span>
                      </p>
                      <p class="c-graph-task-list__title">
                        {{ item.taskDetail | taskTitle }}
                      </p>
                      <p class="c-graph-task-list__button">
                        <Button
                          size="w100"
                          fsize="f12"
                          variant="main"
                          @click.stop="showModalTask(item, dateTask.date)"
                        >
                          タスク詳細
                        </Button>
                      </p>
                    </li>
                  </template>
                </template>
              </ul>
            </perfect-scrollbar>
          </div>
        </template>
        <template v-else>
          <div class="c-graph-task__header c-graph-task__header--active">
            <p class="c-text--ellipsis">
              施策のログ（0件） キーワード: {{ selected.keyword }} URL:
              {{ removedUrl }}
            </p>
          </div>
          <div class="c-graph-task-list c-graph-task-list--empty">
            指定の期間には、完了したタスクがありません。
          </div>
        </template>
      </div>
      <ModalFormProposal
        v-if="task.modal.isActiveProposal"
        :item="task.modal.item"
        :project="project"
        @delete="deleteTask"
        @edit="editTask"
        @close="closeModalTask"
      />
      <ModalFormTask
        v-if="task.modal.isActive"
        :column="task.modal.column"
        :item="task.modal.item"
        mode="edit"
        :project="project"
        @delete="deleteTask"
        @edit="editTask"
        @close="closeModalTask"
      />
    </template>
  </div>
</template>

<script>
import { CALENDAR } from '@/constants';
import { PerfectScrollbar } from 'vue2-perfect-scrollbar';
import Button from '@/components/Button';
import Error from '@/components/Error';
import GraphAxis from '@/components/GraphAxis';
import GraphBg from '@/components/GraphBg';
import GraphFrame from '@/components/GraphFrame';
import GraphMask from '@/components/GraphMask';
import GraphTooltip from '@/components/GraphTooltip';
import Loading from '@/components/Loading';
import ModalFormProposal from '@/components/taskList/ModalFormProposal';
import ModalFormTask from '@/components/taskList/ModalFormTask';
import utils from '@/utils';
import * as d3 from 'd3';

export default {
  props: {
    checked: {
      type: Array,
      required: true
    },
    data: {
      type: Object,
      required: true
    },
    keywordId: {
      type: String,
      required: false
    },
    period: {
      type: Array,
      required: true
    },
    project: {
      type: Object,
      required: true
    },
    tasks: {
      type: Array,
      required: false
    },
    itemData: {
      type: Object,
      required: true
    },
    isLoading: {
      type: Boolean,
      required: false
    },
    selected: {
      type: Object,
      required: false
    }
  },
  components: {
    Button,
    Error,
    GraphAxis,
    GraphBg,
    GraphFrame,
    GraphMask,
    GraphTooltip,
    Loading,
    ModalFormProposal,
    ModalFormTask,
    PerfectScrollbar
  },
  data() {
    return {
      bar: {
        data: [],
        resetData: []
      },
      d3: {
        band: () => {},
        line: () => {},
        x: () => {},
        y: {}
      },
      el: {
        canvas: '',
        svg: '',
        gBar: '',
        gBgPointer: '',
        gDefs: '',
        gLabel: '',
        gLine: '',
        gLineEmpty: '',
        gMarker: '',
        gPointer: ''
      },
      path: {
        data: [],
        emptyData: [],
        resetData: [],
        resetEmptyData: []
      },
      pointer: {
        data: []
      },
      size: {
        axisXH: 40,
        axisYW: {
          left: 20,
          right: 20
        },
        barR: 3,
        barW: 11,
        canvasH: 0,
        canvasW: 0,
        frameW: 1,
        graphH: 0,
        graphW: 0,
        graph: {
          bottom: 0,
          left: 0,
          right: 0,
          top: 0
        },
        markerR: 3.5,
        offset: {
          bottom: 0,
          left: 0,
          right: 0,
          top: 33
        },
        pointerMarkerR: 6,
        pointerW: 0,
        spacerX: 0,
        windowW: window.innerWidth
      },
      task: {
        modal: {
          item: {},
          column: 'todo',
          isActive: false,
          isActiveProposal: false,
          date: null
        },
        selected: null
      },
      tooltip: {
        data: {},
        x: 0,
        y: 0,
        isVisible: false
      },
      transition: {
        duration: 500,
        ease: d3.easeQuad
      },
      x: {
        axis: [],
        band: [],
        bg: [],
        count: 0,
        domain: [],
        scaleType: ''
      },
      y: {
        display: []
      },
      isDrawed: false,
      isDrawing: false
    };
  },

  computed: {
    categoryLabel() {
      return this.$store.getters['task/solutionLabel'];
    },
    cvPathes() {
      return Object.keys(this.itemData.conversion.children);
    },
    validTasks() {
      const startTime = this.period[0].getTime();
      const endTime = this.period[1].getTime();

      // 指定範囲内のタスクのみをフィルター
      return this.tasks.filter(task => {
        const time = utils
          .getDateResetHours(task.typeDoneDate || task.updatedAt)
          .getTime();
        // 指定期間内の日付のみにする
        return time >= startTime && time <= endTime;
      });
    },
    dateTasks() {
      // 同じ日付のタスクをまとめる
      const taskObj = this.validTasks.reduce((acc, obj) => {
        // 指定期間内の日付のみにする
        const date = utils.formatDate(
          obj.typeDoneDate || obj.updatedAt,
          'YYYY-MM-DD'
        );
        if (!acc[date]) {
          acc[date] = [];
        }
        acc[date].push(obj);

        return acc;
      }, {});

      return Object.keys(taskObj)
        .sort()
        .reverse()
        .map(date => {
          return {
            date: utils.getDateResetHours(date),
            items: taskObj[date]
          };
        });
    },
    pathes() {
      return Object.keys(this.itemData);
    },
    hasData() {
      return this.data.items.length ? true : false;
    },
    hasKeyword() {
      return this.keywordId ? true : false;
    },
    removedUrl() {
      return this.$options.filters.removeDomain(
        this.selected.url,
        this.project.siteUrl
      );
    },
    isError() {
      if (!this.hasKeyword || !this.hasData) {
        return true;
      }
      return false;
    }
  },

  watch: {
    checked() {
      if (this.isLoading || !this.isDrawed) {
        return;
      }
      this.y.display = this.getDisplayY();
      this.drawPath();
      this.drawAxisY();
      this.drawLabel();
      this.drawPointer();
    },
    data: {
      handler() {
        this.update();
      },
      deep: true
    }
  },

  mounted() {
    this.setEl();
    this.setSize();
    this.update();
    this.setEvent();
    window.addEventListener('resize', this.resize);
  },
  destroyed() {
    window.removeEventListener('resize', this.resize);
  },

  methods: {
    category(task) {
      let category = '';
      if (this.categoryLabel[task.type]) {
        if (task.isProposal) {
          category += '提案 - ';
        }
        category += this.categoryLabel[task.type];
      }
      return category;
    },
    categoryCssClass(task) {
      return {
        'c-graph-task-list__category': true,
        ['c-graph-task-list__category--' + task.type]: task.type
      };
    },

    update() {
      if (this.isLoading) {
        return;
      }
      this.isDrawing = true;
      this.hideTask();
      this.hideTooltip();
      this.setData();
      this.setScale();
      this.draw();
      this.isDrawed = true;
      this.isDrawing = false;
    },

    setEl() {
      this.el.canvas = this.$refs.canvas;
      this.el.svg = d3.select(this.$refs.svg);
      this.el.gBar = d3.select(this.$refs.gBar);
      this.el.gBgPointer = d3.select(this.$refs.gBgPointer);
      this.el.gDefs = d3.select(this.$refs.defs);
      this.el.gLabel = d3.select(this.$refs.gLabel);
      this.el.gLine = d3.select(this.$refs.gLine);
      this.el.gLineEmpty = d3.select(this.$refs.gLineEmpty);
      this.el.gMarker = d3.select(this.$refs.gMarker);
      this.el.gPointer = d3.select(this.$refs.gPointer);
    },

    setSize() {
      this.size.canvasH = this.el.svg.node().clientHeight;
      this.size.canvasW = this.el.svg.node().clientWidth;

      this.size.graphH =
        this.size.canvasH -
        this.size.axisXH -
        this.size.frameW * 2 -
        this.size.offset.top -
        this.size.offset.bottom;

      this.size.graphW =
        this.size.canvasW -
        this.size.axisYW.left -
        this.size.axisYW.right -
        this.size.frameW * 2 -
        this.size.offset.left -
        this.size.offset.right;

      this.size.graph.bottom =
        this.size.canvasH -
        this.size.axisXH -
        this.size.frameW -
        this.size.offset.bottom;

      this.size.graph.left =
        this.size.offset.left + this.size.axisYW.left + this.size.frameW;

      this.size.graph.right =
        this.size.canvasW -
        this.size.axisYW.right -
        this.size.frameW -
        this.size.offset.right;

      this.size.graph.top = this.size.offset.top + this.size.frameW;
    },

    setEvent() {
      this.el.gPointer
        .on('mousemove', this.mousemovePointer)
        .on('mouseleave', this.hideTooltip);
    },

    setData() {
      this.bar.data = [];
      this.bar.resetData = [];
      this.path.data = [];
      this.path.resetData = [];
      this.path.emptyData = [];
      this.path.resetEmptyData = [];

      this.pointer.data = this.data.items.concat(
        this.getEmptyData(this.data.items)
      );

      for (let i = 0; i < this.pathes.length; i++) {
        const path = this.pathes[i];
        const scaleType = this.itemData[path].type;
        let data = [];

        if (path === 'conversion') {
          for (let j = 0; j < this.cvPathes.length; j++) {
            const cvPath = this.cvPathes[j];
            const cvData = this.data.items.map((item, i) => {
              return {
                cvPath: cvPath,
                path: path,
                date: item.date,
                value: item[cvPath]
              };
            });
            const cvResetData = this.getResetData(cvData, scaleType);
            this.bar.data.push(cvData);
            this.bar.resetData.push(cvResetData);
            data.push(...cvData);
          }
        } else {
          data = this.getPathData(this.data.items, path);
          const emptyData = this.getEmptyData(data);
          const resetData = this.getResetData(data, scaleType);
          const resetEmptyData = this.getResetData(emptyData, scaleType);
          this.path.data.push(data);
          this.path.emptyData.push(emptyData);
          this.path.resetData.push(resetData);
          this.path.resetEmptyData.push(resetEmptyData);
        }

        // コンバージョンのY軸は直接・間接共通にする
        const axisY = this.getAxisY(data, scaleType);
        const bgY = this.getBgY(axisY, scaleType);
        const domainY = this.getDomainY(axisY, scaleType);

        this.y[path] = {
          axis: axisY,
          bg: bgY,
          domain: domainY,
          scaleType: scaleType
        };
      }

      this.x.domain = this.getDomainX();
      this.x.band = this.getBandX();
      this.x.count = d3.timeDay.count(this.x.domain[0], this.x.domain[1]);
      this.x.scaleType = utils.getTimeType(this.x.count, this.size.graphW);
      this.x.bg = this.getBgX();
      this.x.axis = this.getAxisX();

      this.y.display = this.getDisplayY();
    },

    getPathData(data, path) {
      return data.map((item, i) => {
        return {
          path: path,
          date: item.date,
          value: item[path]
        };
      });
    },

    getEmptyData(data) {
      const array = [];
      if (data.length) {
        const lastData = data[data.length - 1];
        const emptyDays = d3.timeDay.count(
          CALENDAR.gsc.maxDate,
          this.period[1]
        );
        let date = new Date(this.period[1]);
        for (let i = 0; i < emptyDays; i++) {
          const item = Object.assign({}, lastData);
          item.date = new Date(date);
          item.isEmpty = true;
          array.unshift(item);
          date.setDate(date.getDate() - 1);
        }
      }
      return array;
    },

    getResetData(data, type) {
      return data.map(item => {
        let obj = Object.assign({}, item);
        if (!utils.isEmpty(obj.value)) {
          // 順位だけ1番下の値が50なので
          if (type === 'rank') {
            obj.value = 50;
          } else {
            obj.value = 0;
          }
        }
        return obj;
      });
    },

    getDomainX() {
      let array = [this.period[0], this.period[1]];
      return array;
    },

    getBandX() {
      let array = d3.timeDays(this.x.domain[0], this.x.domain[1]);
      if (array.length) {
        array.push(this.x.domain[1]);
      } else {
        array = this.x.domain;
      }
      return array;
    },

    getBgX() {
      let array = d3[this.x.scaleType].range(
        this.x.domain[0],
        this.x.domain[1]
      );
      if (array.length) {
        if (this.x.scaleType === 'timeDay') {
          array.push(this.x.domain[1]);
        }
      } else {
        array = [this.x.domain[0]];
      }
      return array;
    },

    getAxisX() {
      let range = d3[this.x.scaleType].range(
        this.x.domain[0],
        this.x.domain[1]
      );
      let max = range.length || 1;
      if (this.x.scaleType === 'timeDay' || this.x.scaleType === 'timeWeek') {
        return max;
      } else {
        return Math.min(max, 5);
      }
    },

    getAxisY(data, scaleType) {
      if (scaleType === 'rank') {
        return [0, 1, 25, 50];
      } else {
        let max =
          d3.max(
            data.map(item => {
              return item.value;
            })
          ) || 0;
        if (scaleType === 'percentage') {
          return utils.getTickValues(5, max, 1);
        } else {
          return utils.getTickValues(5, max + 1);
        }
      }
    },

    getBgY(axis, scaleType) {
      if (scaleType === 'rank') {
        return d3.range(axis[2] / 4, axis[axis.length - 1], axis[2] / 4);
      } else {
        return d3.range(axis[1] / 2, axis[axis.length - 1], axis[1] / 2);
      }
    },

    getDomainY(axis, scaleType) {
      if (scaleType === 'rank') {
        return [axis[axis.length - 1], 0];
      } else {
        return [0, axis[axis.length - 1]];
      }
    },

    getDisplayY() {
      let array = [];
      if (this.checked.length && this.checked.length < 3) {
        for (let i = 0; i < this.checked.length; i++) {
          let item = this.itemData[this.checked[i]];
          item.name = this.checked[i];
          array.push(item);
        }
      }
      return array;
    },

    setScale() {
      // 棒グラフの幅
      this.d3.band = d3
        .scaleBand()
        .domain(this.x.band)
        .range([this.size.graph.left, this.size.graph.right]);

      this.size.barW = Math.ceil(this.d3.band.bandwidth() / 4);
      if (this.size.barW < 1) {
        this.size.barW = 1;
      } else if (this.size.barW > 11) {
        this.size.barW = 11;
      }
      // 棒グラフと棒グラフの間
      this.size.spacerX = this.size.barW > 4 ? 1 : 0;

      // hoverした時の緑背景の幅
      this.size.pointerW = Math.ceil(this.d3.band.bandwidth());

      this.d3.x = d3
        .scaleTime()
        .domain(this.x.domain)
        .rangeRound([
          this.size.graph.left + this.d3.band.bandwidth() / 2,
          this.size.graph.right - this.d3.band.bandwidth() / 2
        ]);

      for (let i = 0; i < this.pathes.length; i++) {
        const path = this.pathes[i];
        this.d3.y[path] = d3
          .scaleLinear()
          .domain(this.y[path].domain)
          .rangeRound([this.size.graph.bottom, this.size.graph.top]);

        this.d3.line[path] = this.getD3Line(this.d3.y[path]);
      }
    },

    getD3Line(scaleY) {
      return d3
        .line()
        .defined(d => {
          return d.value !== null;
        })
        .x((d, i) => {
          return this.d3.x(d.date);
        })
        .y((d, i) => {
          return scaleY(d.value);
        });
    },

    draw() {
      this.drawPath();
      this.$refs.gAxisX.draw();
      this.$refs.gBgX.draw();
      this.drawAxisY();
      this.drawLabel();
      this.drawPointer();
    },

    attrBarGroup(selection) {
      selection.attr('transform', (d, i) => {
        const x =
          -this.size.barW -
          Math.floor(this.size.spacerX / 2) +
          (this.size.barW + this.size.spacerX) * i;
        return `translate(${x}, 0)`;
      });
    },

    attrBar(selection) {
      selection
        .attr('opacity', (d, i) => {
          if (this.checked.indexOf(d.path) === -1) {
            return 0;
          } else {
            return 1;
          }
        })
        .attr('x', (d, i) => {
          return this.d3.x(d.date);
        })
        .attr('y', (d, i) => {
          if (this.checked.indexOf(d.path) === -1) {
            return this.d3.y[d.path](0);
          } else {
            return this.d3.y[d.path](d.value);
          }
        })
        .attr('width', (d, i) => {
          return this.size.barW;
        })
        .attr('height', (d, i) => {
          let h = this.d3.y[d.path](0);
          if (this.checked.indexOf(d.path) !== -1) {
            h -= this.d3.y[d.path](d.value);
          }
          return h + this.size.barR;
        })
        .attr('rx', this.size.barR)
        .attr('ry', this.size.barR);
    },

    attrLine(selection, resetData) {
      selection
        .attr('opacity', (d, i) => {
          if (this.checked.indexOf(this.pathes[i]) === -1) {
            return 0;
          } else {
            return 1;
          }
        })
        .attr('d', (d, i) => {
          if (this.checked.indexOf(this.pathes[i]) === -1) {
            return this.d3.line[this.pathes[i]](resetData[i]);
          } else {
            return this.d3.line[this.pathes[i]](d);
          }
        });
    },

    attrMarker(selection) {
      selection
        .attr('opacity', (d, i, nodes) => {
          if (d.value && this.checked.indexOf(d.path) !== -1) {
            if (
              (!nodes[i - 1] ||
                utils.isEmpty(d3.select(nodes[i - 1]).datum().value)) &&
              (!nodes[i + 1] ||
                utils.isEmpty(d3.select(nodes[i + 1]).datum().value))
            ) {
              return 1;
            }
          }
          return 0;
        })
        .attr('cx', (d, i) => {
          return this.d3.x(d.date);
        })
        .attr('cy', (d, i) => {
          if (this.checked.indexOf(d.path) === -1) {
            return this.d3.y[d.path](0);
          } else {
            return this.d3.y[d.path](d.value);
          }
        });
    },

    drawPath() {
      let transition = d3
        .transition()
        .duration(this.transition.duration)
        .ease(this.transition.ease);

      this.el.gBar
        .selectAll('g')
        .data(this.bar.data)
        .join(
          enter => {
            return enter
              .append('g')
              .call(enter =>
                enter.transition(transition).call(this.attrBarGroup)
              );
          },
          update => {
            return update
              .interrupt()
              .transition(transition)
              .call(this.attrBarGroup);
          },
          exit => {
            return exit.remove();
          }
        )
        .each((d, index, nodes) => {
          d3.select(nodes[index])
            .selectAll('rect')
            .data(this.bar.data[index])
            .join(
              enter => {
                return enter
                  .append('rect')
                  .attr('class', (d, i) => {
                    const color = this.itemData[d.path].children[d.cvPath]
                      .color;
                    return `c-color-fill--${color}`;
                  })
                  .call(enter =>
                    enter
                      .data(this.bar.resetData[index])
                      .call(this.attrBar)
                      .data(this.bar.data[index])
                      .transition(transition)
                      .call(this.attrBar)
                  );
              },
              update => {
                return update
                  .interrupt()
                  .transition(transition)
                  .call(this.attrBar);
              },
              exit => {
                return exit.remove();
              }
            );
        });

      this.el.gLine
        .selectAll('path')
        .data(this.path.data)
        .join(
          enter => {
            return enter
              .append('path')
              .attr('class', (d, i) => {
                const color = this.itemData[this.pathes[i]].color;
                return `c-graph-line__path c-color-stroke--${color}`;
              })
              .call(enter =>
                enter
                  .data(this.path.resetData)
                  .call(this.attrLine, this.path.resetData)
                  .data(this.path.data)
                  .transition(transition)
                  .call(this.attrLine, this.path.resetData)
              );
          },
          update => {
            return update
              .interrupt()
              .transition(transition)
              .call(this.attrLine, this.path.resetData);
          },
          exit => {
            return exit.remove();
          }
        );

      this.el.gLineEmpty
        .selectAll('path')
        .data(this.path.emptyData)
        .join(
          enter => {
            return enter
              .append('path')
              .attr('class', 'c-graph-line__path')
              .call(enter =>
                enter
                  .data(this.path.resetEmptyData)
                  .call(this.attrLine, this.path.resetEmptyData)
                  .data(this.path.emptyData)
                  .transition(transition)
                  .call(this.attrLine, this.path.resetEmptyData)
              );
          },
          update => {
            return update
              .interrupt()
              .transition(transition)
              .call(this.attrLine, this.path.resetEmptyData);
          },
          exit => {
            return exit.remove();
          }
        );

      this.el.gMarker
        .selectAll('g')
        .data(this.path.data)
        .join(
          enter => {
            return enter.append('g');
          },
          update => {
            return update;
          },
          exit => {
            return exit.remove();
          }
        )
        .each((d, index, nodes) => {
          d3.select(nodes[index])
            .selectAll('circle')
            .data(this.path.data[index])
            .join(
              enter => {
                return enter
                  .append('circle')
                  .attr('class', (d, i) => {
                    const color = this.itemData[d.path].color;
                    return `c-graph-marker__circle c-color-stroke--${color}`;
                  })
                  .attr('r', this.size.markerR)
                  .call(enter => {
                    enter
                      .data(this.path.resetData[index])
                      .call(this.attrMarker)
                      .data(this.path.data[index])
                      .transition(transition)
                      .call(this.attrMarker);
                  });
              },
              update => {
                return update
                  .interrupt()
                  .transition(transition)
                  .call(this.attrMarker);
              },
              exit => {
                return exit.remove();
              }
            );
        });
    },

    drawAxisY() {
      if (this.$refs.gAxisYL) {
        this.$refs.gAxisYL.draw();
      }
      if (this.$refs.gAxisYR) {
        this.$refs.gAxisYR.draw();
      }
      if (this.$refs.gBgY) {
        this.$refs.gBgY.draw();
      }
    },

    drawLabel() {
      let attrLabel = selection => {
        selection
          .text((d, i) => {
            return d.label;
          })
          .attr('x', (d, i) => {
            return i === 0 ? 0 : this.size.canvasW;
          })
          .attr('y', 22)
          .attr('text-anchor', (d, i) => {
            return i === 0 ? 'start' : 'end';
          });
      };

      this.el.gLabel
        .selectAll('text')
        .data(this.y.display)
        .join(
          enter => {
            return enter.append('text').call(enter =>
              enter
                .attr('opacity', 0)
                .call(attrLabel)
                .transition()
                .duration(this.transition.duration)
                .ease(this.transition.ease)
                .attr('opacity', 1)
            );
          },
          update => {
            return update
              .interrupt()
              .attr('opacity', 0)
              .call(attrLabel)
              .transition()
              .duration(this.transition.duration)
              .ease(this.transition.ease)
              .attr('opacity', 1);
          },
          exit => {
            return exit.remove();
          }
        );
    },

    attrPointer(selection) {
      selection
        .classed('is-empty', (d, i) => {
          return d.isEmpty;
        })
        .attr('transform', (d, i) => {
          return `translate(${this.d3.x(d.date)}, 0)`;
        });
    },

    attrPointerLine(selection) {
      selection
        .classed('is-hidden', (d, i) => {
          return !d.isEmpty;
        })
        .attr('x1', 0)
        .attr('x2', 0)
        .attr('y1', 0)
        .attr('y2', this.size.canvasH);
    },

    attrPointerMarker(selection) {
      selection
        .attr('display', (d, i) => {
          if (utils.isEmpty(d.value) || this.checked.indexOf(d.path) == -1) {
            return 'none';
          }
        })
        .attr('cx', (d, i) => {
          if (d.cvPath === 'directConversion') {
            return -this.size.barW / 2 - Math.floor(this.size.spacerX / 2);
          } else if (d.cvPath === 'assistedConversion') {
            return this.size.barW / 2 + this.size.spacerX;
          }
          return 0;
        })
        .attr('cy', (d, i) => {
          return this.d3.y[d.path](d.value);
        })
        .attr('r', this.size.pointerMarkerR);
    },

    attrBgPointer(selection) {
      selection
        .classed('is-empty', (d, i) => {
          return d.isEmpty;
        })
        .attr('x', (d, i) => {
          let x = Math.ceil(this.d3.band(d.date));
          // d3.bandのdomain外の日付の場合、undefinedが返るため
          return x ? x : 0;
        })
        .attr('y', this.size.graph.top)
        .attr('height', this.size.graphH)
        .attr('width', (d, i) => {
          return this.size.pointerW;
        });
    },

    drawPointer() {
      this.el.gPointer
        .selectAll('g')
        .data(this.pointer.data)
        .join(
          enter => {
            return enter.append('g').call(this.attrPointer);
          },
          update => {
            return update.call(this.attrPointer);
          },
          exit => {
            return exit.remove();
          }
        )
        .each((d, index, nodes) => {
          let data = [];
          for (let i = 0; i < this.pathes.length; i++) {
            const path = this.pathes[i];
            if (path === 'conversion') {
              // 空白期間のコンバージョンのマーカーは表示しない
              if (d.isEmpty) {
                continue;
              }
              for (let j = 0; j < this.cvPathes.length; j++) {
                const cvPath = this.cvPathes[j];
                data.push({
                  cvPath: cvPath,
                  path: path,
                  date: d.date,
                  value: d[cvPath]
                });
              }
            } else {
              data.push({
                path: path,
                date: d.date,
                value: d[path],
                isEmpty: d.isEmpty
              });
            }
          }

          d3.select(nodes[index])
            .selectAll('line')
            .data(data)
            .join(
              enter => {
                return enter
                  .append('line')
                  .attr('class', 'c-graph-pointer__line')
                  .call(this.attrPointerLine);
              },
              update => {
                return update.call(this.attrPointerLine);
              },
              exit => {
                return exit.remove();
              }
            );

          d3.select(nodes[index])
            .selectAll('circle')
            .data(data)
            .join(
              enter => {
                return enter
                  .append('circle')
                  .attr('class', 'c-graph-pointer__marker')
                  .call(this.attrPointerMarker);
              },
              update => {
                return update.call(this.attrPointerMarker);
              },
              exit => {
                return exit.remove();
              }
            );
        });

      this.el.gBgPointer
        .selectAll('rect')
        .data(this.pointer.data)
        .join(
          enter => {
            return enter
              .append('rect')
              .attr('class', 'c-graph-bg-pointer__rect')
              .call(this.attrBgPointer);
          },
          update => {
            return update.call(this.attrBgPointer);
          },
          exit => {
            return exit.remove();
          }
        );
    },

    mousemovePointer() {
      if (!this.isDrawed) {
        return;
      }
      let isHovered = false;

      this.el.gPointer
        .selectAll('g')
        .classed('is-hover', false)
        .each((d, i, nodes) => {
          if (isHovered) {
            return;
          }
          const x = this.d3.x(d.date);
          const start = x - this.size.pointerW / 2;
          const end = x + this.size.pointerW / 2;
          if (d3.event.offsetX >= start && d3.event.offsetX <= end) {
            d3.select(nodes[i]).classed('is-hover', true);
            this.showTooltip(d, x, d3.event.offsetY);

            const date = d.date;
            this.el.gBgPointer.selectAll('rect').classed('is-hover', (d, i) => {
              if (d.date.getTime() === date.getTime()) {
                isHovered = true;
                return true;
              }
            });
          }
        });

      if (!isHovered) {
        this.hideTooltip();
      }
    },

    mouseOverTaskMarker(event, date, offsetX) {
      if (!this.isDrawed) {
        return;
      }
      let isHovered = false;

      this.el.gPointer
        .selectAll('g')
        .classed('is-hover', false)
        .each((d, i, nodes) => {
          if (isHovered) {
            return;
          }
          const x = this.d3.x(d.date);
          const start = x - this.size.pointerW / 2;
          const end = x + this.size.pointerW / 2;
          //グラフ要素左上からの相対座標を取得する
          const y =
            event.clientY - this.$refs.gPointer.getBoundingClientRect().top;
          if (offsetX >= start && offsetX <= end) {
            d3.select(nodes[i]).classed('is-hover', true);
            this.showTooltip(d, x, y);

            const date = d.date;
            this.el.gBgPointer.selectAll('rect').classed('is-hover', (d, i) => {
              if (d.date.getTime() === date.getTime()) {
                isHovered = true;
                return true;
              }
            });
          }
        });

      if (!isHovered) {
        this.hideTooltip();
      }
    },

    showTooltip(d, x, y) {
      this.tooltip.data = d;
      this.tooltip.x = x;
      this.tooltip.y = y;
      this.tooltip.isVisible = true;
    },

    hideTooltip() {
      this.tooltip.isVisible = false;
      this.el.gPointer.selectAll('g').classed('is-hover', false);
      this.el.gBgPointer.selectAll('rect').classed('is-hover', false);
    },

    toggleTask(task) {
      if (!this.$refs.taskMarker || !this.$refs.taskMarker.length) {
        return;
      }
      if (task === this.task.selected) {
        this.task.selected = null;
        for (let i = 0; i < this.$refs.taskMarker.length; i++) {
          this.$refs.taskMarker[i].classList.remove(
            'c-graph-task-marker__item--is-selected'
          );
        }
      } else {
        this.task.selected = task;
        for (let i = 0; i < this.$refs.taskMarker.length; i++) {
          if (
            utils.formatDate(this.task.selected.date, 'YYYY.MM.DD') ==
            this.$refs.taskMarker[i].dataset.date
          ) {
            this.$refs.taskMarker[i].classList.add(
              'c-graph-task-marker__item--is-selected'
            );
          } else {
            this.$refs.taskMarker[i].classList.remove(
              'c-graph-task-marker__item--is-selected'
            );
          }
        }

        // クリックされた要素まで移動する処理 ($refの中身は日付でソートされていないので2回ループを回して移動位置を計算する)
        let list = {};
        for (let i = 0; i < this.$refs.taskListItem.length; i++) {
          if (list[this.$refs.taskListItem[i].dataset.date]) {
            list[
              this.$refs.taskListItem[i].dataset.date
            ] += this.$refs.taskListItem[i].clientHeight;
          } else {
            list[
              this.$refs.taskListItem[i].dataset.date
            ] = this.$refs.taskListItem[i].clientHeight;
          }
        }
        let keys = Object.keys(list)
          .sort()
          .reverse();

        let total = 0;
        for (let i = 0; i < keys.length; i++) {
          if (utils.formatDate(task.date, 'YYYY.MM.DD') == keys[i]) {
            break;
          }
          total += list[keys[i]];
        }

        this.$refs.scroll.$el.scrollTop = total;
      }
    },

    isSelectTask(date) {
      if (!this.task.selected) return false;

      return (
        utils.formatDate(this.task.selected.date, 'YYYY.MM.DD') ===
        utils.formatDate(date, 'YYYY.MM.DD')
      );
    },

    closeTaskList() {
      if (
        this.$refs.btnTaskList.classList.contains(
          'c-graph-task__header--active'
        )
      ) {
        this.$refs.btnTaskList.classList.remove('c-graph-task__header--active');
        this.$refs.taskList.classList.remove(
          'c-graph-task-list-container--active'
        );
      } else {
        this.$refs.btnTaskList.classList.add('c-graph-task__header--active');
        this.$refs.taskList.classList.add(
          'c-graph-task-list-container--active'
        );
      }
    },

    mouseoverTaskList(taskDate) {
      if (!this.isDrawed) {
        return;
      }
      let isHovered = false;

      this.el.gPointer
        .selectAll('g')
        .classed('is-hover', false)
        .each((d, i, nodes) => {
          if (isHovered) {
            return;
          }
          const x = this.d3.x(d.date);

          if (
            utils.formatDate(taskDate, 'YYYY.MM.DD') ===
            utils.formatDate(d.date, 'YYYY.MM.DD')
          ) {
            d3.select(nodes[i]).classed('is-hover', true);
            this.showTooltip(d, x, 100);

            const date = d.date;
            this.el.gBgPointer.selectAll('rect').classed('is-hover', (d, i) => {
              if (d.date.getTime() === date.getTime()) {
                isHovered = true;
                return true;
              }
            });
          }
        });

      if (!isHovered) {
        this.hideTooltip();
      }
    },

    mouseoutTaskList() {
      if (!this.isDrawed) {
        return;
      }
      this.hideTooltip();
      this.el.gPointer
        .selectAll('g')
        .classed('is-hover', false)
        .each((d, i, nodes) => {
          d3.select(nodes[i]).classed('is-hover', false);
          this.el.gBgPointer.selectAll('rect').classed('is-hover', (d, i) => {
            return false;
          });
        });
    },

    hideTask() {
      this.task.selected = null;
    },

    showModalTask(item, date) {
      this.task.modal.item = item;
      if (item.isProposal) {
        this.task.modal.isActiveProposal = true;
      } else {
        this.task.modal.isActive = true;
      }
      this.task.modal.date = date;
    },
    closeModalTask() {
      this.task.modal.column = '';
      this.task.modal.item = {};
      this.task.modal.isActive = false;
      this.task.modal.isActiveProposal = false;
      this.task.modal.date = null;
    },
    editTask(task) {
      const index = this.tasks.findIndex(item => item.id === task.id);
      this.tasks.splice(index, 1, task);
      this.closeModalTask();
    },
    deleteTask(task) {
      const index = this.tasks.findIndex(item => item.id === task.id);
      this.tasks.splice(index, 1);
      this.closeModalTask();
    },
    resize() {
      this.setSize();

      if (!this.isDrawed) {
        return;
      }
      if (this.size.windowW !== window.innerWidth) {
        this.size.windowW = window.innerWidth;
        this.setScale();

        this.el.gBar
          .selectAll('g')
          .call(this.attrBarGroup)
          .selectAll('rect')
          .call(this.attrBar);
        this.el.gLine
          .selectAll('path')
          .call(this.attrLine, this.path.resetData);
        this.el.gLineEmpty
          .selectAll('path')
          .call(this.attrLine, this.path.resetEmptyData);
        this.el.gMarker
          .selectAll('g')
          .selectAll('circle')
          .call(this.attrMarker);
        this.el.gPointer.selectAll('g').call(this.attrPointer);
        this.el.gBgPointer.selectAll('rect').call(this.attrBgPointer);

        this.$refs.gAxisX.resize();
        this.$refs.gBgX.resize();

        if (this.$refs.gAxisYR) {
          this.$refs.gAxisYR.resize();
        }
        if (this.$refs.gBgY) {
          this.$refs.gBgY.resize();
        }

        this.el.gLabel.selectAll('text').attr('x', (d, i) => {
          return i === 0 ? 0 : this.size.canvasW;
        });
      }
    }
  }
};
</script>
